'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Button from '@/components/ui/Button';
import { createClient } from '@/lib/supabase/client';

// ─── Types ───────────────────────────────────────────────────────────────────
interface FormData {
  // Step 1
  selectedPackage: string;
  // Step 2
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  websiteUrl: string;
  industry: string;
  yearsInBusiness: string;
  ownsBusiness: boolean;
  // Step 3
  monthlyMarketingBudget: string;
  monthlyAdSpend: string;
  // Step 4
  brandName: string;
  confirmsNotTrademarked: boolean;
  confirmsNoBrandConflicts: boolean;
  confirmsOwnsAssets: boolean;
  // Step 5
  projectContext: string;
  founderTeamSize: string;
  currentRevenue: string;
  currentBottleneck: string;
  preferredTimeline: string;
  goals90Days: string;
  biggestChallenge: string;
  howHeard: string;
  preferredContact: string;
  anythingElse: string;
}

const INITIAL_FORM: FormData = {
  selectedPackage: '',
  fullName: '',
  email: '',
  phone: '',
  companyName: '',
  websiteUrl: '',
  industry: '',
  yearsInBusiness: '',
  ownsBusiness: false,
  monthlyMarketingBudget: '',
  monthlyAdSpend: '',
  brandName: '',
  confirmsNotTrademarked: false,
  confirmsNoBrandConflicts: false,
  confirmsOwnsAssets: false,
  projectContext: '',
  founderTeamSize: '',
  currentRevenue: '',
  currentBottleneck: '',
  preferredTimeline: '',
  goals90Days: '',
  biggestChallenge: '',
  howHeard: '',
  preferredContact: '',
  anythingElse: '',
};

const PACKAGES = [
  {
    id: 'sprint',
    name: 'Sprint / Diagnostic',
    price: '$2,500 – $15k+',
    description: 'A paid strategic diagnostic. We audit your business, identify the highest-value bottlenecks, and deliver a prioritized roadmap — before you commit to anything else. Typically $5k–$10k. Entry at $2,500 for smaller businesses.',
    best: 'The recommended starting point for all new engagements',
  },
  {
    id: 'buildout',
    name: 'Buildout / Project',
    price: '$10k – $75k+',
    description: 'Fixed-scope implementation based on sprint findings. When the diagnostic uncovers a clear buildout — website, funnel, automation, content system — we scope and execute it as a defined project.',
    best: 'Businesses with a clear implementation need identified in a sprint',
  },
  {
    id: 'ongoing-execution',
    name: 'Ongoing Execution',
    price: '$3k – $10k+/mo',
    description: 'Monthly advisory, execution, and optimization. We become your execution partner — shipping every week, optimizing every month. Depth and access scale with your needs.',
    best: 'Operators who want a consistent execution partner after the sprint',
  },
  {
    id: 'diy-portal',
    name: 'DIY / Portal',
    price: '$500 – $1,500/mo',
    description: 'Templates, systems, dashboards, prompt packs, SOPs, and frameworks — the scalable recurring layer for operators who want the playbook without the retainer.',
    best: 'Founders who want to execute themselves with our systems',
  },
];

const STEPS = [
  { number: 1, label: 'Package' },
  { number: 2, label: 'Business' },
  { number: 3, label: 'Budget' },
  { number: 4, label: 'Legal' },
  { number: 5, label: 'Details' },
];

// ─── Budget disqualification logic ───────────────────────────────────────────
function isDisqualified(budget: string, adSpend: string): boolean {
  const lowBudgets = ['Under $2,500/mo', '$2,500–$5,000/mo'];
  const lowAdSpend = ['No paid ads', 'Under $1,000/mo'];
  return lowBudgets.includes(budget) || lowAdSpend.includes(adSpend);
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function BookPage() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDiyModal, setShowDiyModal] = useState(false);

  const progress = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  // ─── Field helpers ─────────────────────────────────────────────────────────
  const set = (field: keyof FormData, value: string | boolean) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const clearError = (field: string) =>
    setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });

  // ─── Validation per step ───────────────────────────────────────────────────
  function validateStep(step: number): boolean {
    const errs: Record<string, string> = {};
    if (step === 1) {
      if (!formData.selectedPackage) errs.selectedPackage = 'Please select a package or choose Not sure yet.';
    }
    if (step === 2) {
      if (!formData.fullName.trim()) errs.fullName = 'Full name is required.';
      if (!formData.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(formData.email)) errs.email = 'A valid email is required.';
      if (!formData.companyName.trim()) errs.companyName = 'Company name is required.';
      if (!formData.industry) errs.industry = 'Please select your industry.';
      if (!formData.yearsInBusiness) errs.yearsInBusiness = 'Please select years in business.';
      if (!formData.ownsBusiness) errs.ownsBusiness = 'You must confirm you own or operate this business.';
    }
    if (step === 3) {
      if (!formData.monthlyMarketingBudget) errs.monthlyMarketingBudget = 'Please select your monthly marketing budget.';
      if (!formData.monthlyAdSpend) errs.monthlyAdSpend = 'Please select your monthly ad spend.';
    }
    if (step === 4) {
      if (!formData.brandName.trim()) errs.brandName = 'Brand name is required.';
      if (!formData.confirmsNotTrademarked) errs.confirmsNotTrademarked = 'You must confirm this.';
      if (!formData.confirmsNoBrandConflicts) errs.confirmsNoBrandConflicts = 'You must confirm this.';
      if (!formData.confirmsOwnsAssets) errs.confirmsOwnsAssets = 'You must confirm this.';
    }
    if (step === 5) {
      if (!formData.projectContext.trim()) errs.projectContext = 'Please describe your project context.';
      if (!formData.founderTeamSize) errs.founderTeamSize = 'Please select your founder/team size.';
      if (!formData.currentRevenue) errs.currentRevenue = 'Please select your current revenue range.';
      if (!formData.currentBottleneck.trim()) errs.currentBottleneck = 'Please describe your current bottleneck.';
      if (!formData.preferredTimeline) errs.preferredTimeline = 'Please select your preferred timeline.';
      if (!formData.goals90Days.trim()) errs.goals90Days = 'Please share your 90-day goals.';
      if (!formData.biggestChallenge.trim()) errs.biggestChallenge = 'Please describe your biggest challenge.';
      if (!formData.howHeard) errs.howHeard = 'Please let us know how you heard about us.';
      if (!formData.preferredContact) errs.preferredContact = 'Please select a preferred contact method.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  // ─── Navigation ────────────────────────────────────────────────────────────
  async function handleNext() {
    if (!validateStep(currentStep)) return;

    // Budget gate at end of step 3
    if (currentStep === 3) {
      if (isDisqualified(formData.monthlyMarketingBudget, formData.monthlyAdSpend)) {
        await saveDisqualified();
        return;
      }
    }

    setCurrentStep((s) => Math.min(s + 1, 5));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleBack() {
    setCurrentStep((s) => Math.max(s - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ─── Save disqualified lead ─────────────────────────────────────────────────
  async function saveDisqualified() {
    setIsSubmitting(true);
    try {
      const supabase = createClient();
      await supabase.from('applications').insert({
        selected_package: formData.selectedPackage,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.companyName,
        website_url: formData.websiteUrl,
        industry: formData.industry,
        years_in_business: formData.yearsInBusiness,
        owns_business: formData.ownsBusiness,
        monthly_marketing_budget: formData.monthlyMarketingBudget,
        monthly_ad_spend: formData.monthlyAdSpend,
        status: 'disqualified_budget',
      });

      // Tier 1: fire lead notification + DIY qualifier email
      fetch('/api/submit-book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData: {
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            company_name: formData.companyName,
            website_url: formData.websiteUrl,
            industry: formData.industry,
            selected_package: formData.selectedPackage,
            monthly_marketing_budget: formData.monthlyMarketingBudget,
            monthly_ad_spend: formData.monthlyAdSpend,
          },
          isDisqualified: true,
        }),
      }).catch(() => {});
    } catch (_) {
      // Silently continue
    } finally {
      setIsSubmitting(false);
      setShowDiyModal(true);
    }
  }

  // ─── Final submit ──────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!validateStep(5)) return;
    setIsSubmitting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.from('applications').insert({
        selected_package: formData.selectedPackage,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company_name: formData.companyName,
        website_url: formData.websiteUrl,
        industry: formData.industry,
        years_in_business: formData.yearsInBusiness,
        owns_business: formData.ownsBusiness,
        monthly_marketing_budget: formData.monthlyMarketingBudget,
        monthly_ad_spend: formData.monthlyAdSpend,
        brand_name: formData.brandName,
        confirms_not_trademarked: formData.confirmsNotTrademarked,
        confirms_no_brand_conflicts: formData.confirmsNoBrandConflicts,
        confirms_owns_assets: formData.confirmsOwnsAssets,
        project_context: formData.projectContext,
        founder_team_size: formData.founderTeamSize,
        current_revenue: formData.currentRevenue,
        current_bottleneck: formData.currentBottleneck,
        preferred_timeline: formData.preferredTimeline,
        goals_90_days: formData.goals90Days,
        biggest_challenge: formData.biggestChallenge,
        how_heard: formData.howHeard,
        preferred_contact: formData.preferredContact,
        anything_else: formData.anythingElse,
        status: 'pending_review',
      });
      if (error) throw error;

      // Tier 1: fire lead notification + auto-response email
      fetch('/api/submit-book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formData: {
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            company_name: formData.companyName,
            website_url: formData.websiteUrl,
            industry: formData.industry,
            selected_package: formData.selectedPackage,
            monthly_marketing_budget: formData.monthlyMarketingBudget,
            monthly_ad_spend: formData.monthlyAdSpend,
            current_revenue: formData.currentRevenue,
            project_context: formData.projectContext,
            biggest_challenge: formData.biggestChallenge,
          },
          isDisqualified: false,
        }),
      }).catch(() => {});

      router.push('/thank-you?type=application');
    } catch (_) {
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }

  // ─── DIY Modal ─────────────────────────────────────────────────────────────
  const renderDiyModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowDiyModal(false)} />
      <div className="relative bg-[#0f0f0f] border border-[#2a2a2a] max-w-[520px] w-full p-8 z-10">
        <button
          onClick={() => setShowDiyModal(false)}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3 font-semibold">Heads up</p>
        <h2 className="text-2xl font-bold mb-4">Your budget is below our minimum</h2>
        <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
          Our sprint and execution engagements are built for founder-led businesses ready to invest at least <span className="text-foreground font-semibold">$2,500/mo</span> in marketing and <span className="text-foreground font-semibold">$1,000/mo</span> in ad spend.
        </p>
        <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
          That said, you're welcome to continue your application — we review every submission personally and may still be a fit depending on your situation. Or, if you'd like to explore our self-service resources first, our DIY portal has everything you need to execute on your own.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => { setShowDiyModal(false); setCurrentStep((s) => Math.min(s + 1, 5)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex-1 px-5 py-3 bg-primary text-black font-semibold text-sm tracking-wide hover:bg-primary/90 transition-colors"
          >
            Continue Application
          </button>
          <button
            onClick={() => router.push('/diy?reason=budget')}
            className="flex-1 px-5 py-3 border border-[#2a2a2a] text-foreground font-semibold text-sm tracking-wide hover:border-[#444] transition-colors"
          >
            Explore DIY Portal
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Shared input classes ──────────────────────────────────────────────────
  const inputCls = (field: string) =>
    `w-full px-4 py-3 bg-[#0a0a0a] border ${
      errors[field] ? 'border-red-500' : 'border-[#2a2a2a] focus:border-primary'
    } text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-colors`;

  const labelCls = 'block text-[10px] uppercase tracking-[0.2em] text-foreground mb-2 font-semibold';
  const errorCls = 'text-red-400 text-xs mt-1';

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">

        {/* ── DIY Modal ── */}
        {showDiyModal && renderDiyModal()}

        {/* ── Hero ── */}
        <section className="py-16 px-6 border-b border-[#1a1a1a]">
          <div className="max-w-[760px] mx-auto text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-4 font-semibold">Exclusive Partnership</p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              Apply to Work<br />With Us
            </h1>
            <p className="text-muted-foreground text-lg max-w-[520px] mx-auto">
              We work with a select number of clients at a time. Complete this application and our team will review it within 1 business day.
            </p>
          </div>
        </section>

        {/* ── Progress Bar ── */}
        <div className="sticky top-[72px] z-30 bg-background border-b border-[#1a1a1a]">
          <div className="max-w-[760px] mx-auto px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              {STEPS.map((step) => (
                <div key={step.number} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                      step.number < currentStep
                        ? 'bg-primary text-black'
                        : step.number === currentStep
                        ? 'bg-primary text-black ring-2 ring-primary ring-offset-2 ring-offset-background' :'bg-[#1a1a1a] text-muted-foreground'
                    }`}
                  >
                    {step.number < currentStep ? (
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>
                  <span className={`text-[9px] uppercase tracking-widest hidden sm:block ${
                    step.number === currentStep ? 'text-primary' : 'text-muted-foreground'
                  }`}>{step.label}</span>
                </div>
              ))}
            </div>
            <div className="h-0.5 bg-[#1a1a1a] rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* ── Form Body ── */}
        <section className="py-12 px-6">
          <div className="max-w-[760px] mx-auto">

            {/* ── STEP 1: Package Interest ── */}
            {currentStep === 1 && (
              <div>
                <div className="mb-10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-2 font-semibold">Step 1 of 5</p>
                  <h2 className="text-3xl font-bold mb-3">Which plan interests you?</h2>
                  <p className="text-muted-foreground">Select the retainer that best fits where your business is headed. Not sure? That's fine too.</p>
                </div>

                <div className="space-y-4">
                  {PACKAGES.map((pkg) => (
                    <label
                      key={pkg.id}
                      className={`block cursor-pointer border p-6 transition-all duration-200 ${
                        formData.selectedPackage === pkg.id
                          ? 'border-primary bg-[#0f0a0a]'
                          : 'border-[#2a2a2a] hover:border-[#444]'
                      }`}
                    >
                      <input
                        type="radio"
                        name="package"
                        value={pkg.id}
                        checked={formData.selectedPackage === pkg.id}
                        onChange={() => { set('selectedPackage', pkg.id); clearError('selectedPackage'); }}
                        className="sr-only"
                      />
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                            formData.selectedPackage === pkg.id ? 'border-primary' : 'border-[#444]'
                          }`}>
                            {formData.selectedPackage === pkg.id && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-3 mb-1">
                              <span className="font-bold text-lg">{pkg.name}</span>
                              <span className="text-primary font-semibold text-sm">{pkg.price}</span>
                            </div>
                            <p className="text-muted-foreground text-sm mb-2">{pkg.description}</p>
                            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                              Best for: <span className="text-foreground">{pkg.best}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </label>
                  ))}

                  {/* Not sure option */}
                  <label
                    className={`block cursor-pointer border p-5 transition-all duration-200 ${
                      formData.selectedPackage === 'not-sure' ?'border-primary bg-[#0f0a0a]' :'border-[#2a2a2a] hover:border-[#444]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="package"
                      value="not-sure"
                      checked={formData.selectedPackage === 'not-sure'}
                      onChange={() => { set('selectedPackage', 'not-sure'); clearError('selectedPackage'); }}
                      className="sr-only"
                    />
                    <div className="flex items-center gap-4">
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        formData.selectedPackage === 'not-sure' ? 'border-primary' : 'border-[#444]'
                      }`}>
                        {formData.selectedPackage === 'not-sure' && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div>
                        <span className="font-semibold">Not sure yet</span>
                        <p className="text-muted-foreground text-sm">I'd like guidance on which plan fits my business.</p>
                      </div>
                    </div>
                  </label>
                </div>

                {errors.selectedPackage && <p className={errorCls}>{errors.selectedPackage}</p>}
              </div>
            )}

            {/* ── STEP 2: Business Basics ── */}
            {currentStep === 2 && (
              <div>
                <div className="mb-10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-2 font-semibold">Step 2 of 5</p>
                  <h2 className="text-3xl font-bold mb-3">Tell us about your business</h2>
                  <p className="text-muted-foreground">We review every application personally. This helps us understand who you are before we connect.</p>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <input className={inputCls('fullName')} value={formData.fullName} onChange={(e) => { set('fullName', e.target.value); clearError('fullName'); }} placeholder="Jane Smith" />
                      {errors.fullName && <p className={errorCls}>{errors.fullName}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Email Address *</label>
                      <input type="email" className={inputCls('email')} value={formData.email} onChange={(e) => { set('email', e.target.value); clearError('email'); }} placeholder="jane@company.com" />
                      {errors.email && <p className={errorCls}>{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Phone Number</label>
                      <input type="tel" className={inputCls('phone')} value={formData.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(702) 546-6337" />
                    </div>
                    <div>
                      <label className={labelCls}>Company Name *</label>
                      <input className={inputCls('companyName')} value={formData.companyName} onChange={(e) => { set('companyName', e.target.value); clearError('companyName'); }} placeholder="Acme Corp" />
                      {errors.companyName && <p className={errorCls}>{errors.companyName}</p>}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Website URL</label>
                    <input type="url" className={inputCls('websiteUrl')} value={formData.websiteUrl} onChange={(e) => set('websiteUrl', e.target.value)} placeholder="https://yourwebsite.com" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Industry *</label>
                      <select className={inputCls('industry')} value={formData.industry} onChange={(e) => { set('industry', e.target.value); clearError('industry'); }}>
                        <option value="">Select your industry</option>
                        <option value="ecommerce">Ecommerce</option>
                        <option value="restaurant-hospitality">Restaurant / Hospitality</option>
                        <option value="real-estate">Real Estate</option>
                        <option value="fitness-health">Fitness / Health</option>
                        <option value="professional-services">Professional Services</option>
                        <option value="entertainment">Entertainment</option>
                        <option value="retail">Retail</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.industry && <p className={errorCls}>{errors.industry}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Years in Business *</label>
                      <select className={inputCls('yearsInBusiness')} value={formData.yearsInBusiness} onChange={(e) => { set('yearsInBusiness', e.target.value); clearError('yearsInBusiness'); }}>
                        <option value="">Select range</option>
                        <option value="less-than-1">Less than 1 year</option>
                        <option value="1-2">1–2 years</option>
                        <option value="3-5">3–5 years</option>
                        <option value="6-10">6–10 years</option>
                        <option value="10+">10+ years</option>
                      </select>
                      {errors.yearsInBusiness && <p className={errorCls}>{errors.yearsInBusiness}</p>}
                    </div>
                  </div>

                  <div>
                    <label className={`flex items-start gap-3 cursor-pointer group`}>
                      <div
                        className={`mt-0.5 w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                          formData.ownsBusiness ? 'border-primary bg-primary' : 'border-[#444] group-hover:border-[#666]'
                        }`}
                        onClick={() => { set('ownsBusiness', !formData.ownsBusiness); clearError('ownsBusiness'); }}
                      >
                        {formData.ownsBusiness && (
                          <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-foreground">
                        I confirm that I own or operate this business and am authorized to make marketing decisions on its behalf.
                      </span>
                    </label>
                    {errors.ownsBusiness && <p className={`${errorCls} mt-1`}>{errors.ownsBusiness}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Budget Qualifier ── */}
            {currentStep === 3 && (
              <div>
                <div className="mb-10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-2 font-semibold">Step 3 of 5</p>
                  <h2 className="text-3xl font-bold mb-3">Investment capacity</h2>
                  <p className="text-muted-foreground">We work with founder-led service businesses doing $500K–$5M/year who are ready to invest in sprint-based execution. This helps us match you to the right engagement level.</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className={labelCls}>Monthly Marketing Budget *</label>
                    <select className={inputCls('monthlyMarketingBudget')} value={formData.monthlyMarketingBudget} onChange={(e) => { set('monthlyMarketingBudget', e.target.value); clearError('monthlyMarketingBudget'); }}>
                      <option value="">Select your monthly budget</option>
                      <option value="Under $2,500/mo">Under $2,500 / mo</option>
                      <option value="$2,500–$5,000/mo">$2,500 – $5,000 / mo (Sprint range)</option>
                      <option value="$5,000–$10,000/mo">$5,000 – $10,000 / mo (Buildout range)</option>
                      <option value="$10,000–$20,000/mo">$10,000 – $20,000 / mo (Execution range)</option>
                      <option value="$20,000+/mo">$20,000+ / mo (Full-scale)</option>
                    </select>
                    {errors.monthlyMarketingBudget && <p className={errorCls}>{errors.monthlyMarketingBudget}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>Monthly Ad Spend *</label>
                    <select className={inputCls('monthlyAdSpend')} value={formData.monthlyAdSpend} onChange={(e) => { set('monthlyAdSpend', e.target.value); clearError('monthlyAdSpend'); }}>
                      <option value="">Select your ad spend</option>
                      <option value="No paid ads">No paid ads currently</option>
                      <option value="Under $1,000/mo">Under $1,000 / mo</option>
                      <option value="$1,000–$3,000/mo">$1,000 – $3,000 / mo (Entry)</option>
                      <option value="$3,000–$7,500/mo">$3,000 – $7,500 / mo (Sprint range)</option>
                      <option value="$7,500–$15,000/mo">$7,500 – $15,000 / mo (Buildout range)</option>
                      <option value="$15,000–$40,000/mo">$15,000 – $40,000 / mo (Execution range)</option>
                      <option value="$40,000+/mo">$40,000+ / mo (Full-scale)</option>
                    </select>
                    {errors.monthlyAdSpend && <p className={errorCls}>{errors.monthlyAdSpend}</p>}
                  </div>

                  <div className="border border-[#2a2a2a] p-5 bg-[#080808]">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Why we ask</p>
                    <p className="text-sm text-muted-foreground">
                      Our sprint and execution engagements are built for founder-led businesses ready to invest in real growth infrastructure. If your current budget doesn't align with our minimums, we'll point you toward self-service resources that may be a better fit right now.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 4: Business Name & Legal ── */}
            {currentStep === 4 && (
              <div>
                <div className="mb-10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-2 font-semibold">Step 4 of 5</p>
                  <h2 className="text-3xl font-bold mb-3">Brand & legal clarity</h2>
                  <p className="text-muted-foreground">We take brand integrity seriously. Before we invest in building your brand, we need to confirm your name and assets are clear to use.</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className={labelCls}>Business / Brand Name *</label>
                    <input className={inputCls('brandName')} value={formData.brandName} onChange={(e) => { set('brandName', e.target.value); clearError('brandName'); }} placeholder="Your brand name as it will appear publicly" />
                    {errors.brandName && <p className={errorCls}>{errors.brandName}</p>}
                  </div>

                  <div className="space-y-4 pt-2">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Legal Confirmations</p>

                    {[
                      { field: 'confirmsNotTrademarked' as keyof FormData, label: 'This business name is not trademarked by another party, to the best of my knowledge.' },
                      { field: 'confirmsNoBrandConflicts' as keyof FormData, label: 'There are no known brand conflicts with similar names in my industry or market.' },
                      { field: 'confirmsOwnsAssets' as keyof FormData, label: 'I own the rights to any logos, images, and creative assets I will provide to Best Odds Corp.' },
                    ].map(({ field, label }) => (
                      <div key={field}>
                        <label className="flex items-start gap-3 cursor-pointer group">
                          <div
                            className={`mt-0.5 w-5 h-5 border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                              formData[field] ? 'border-primary bg-primary' : 'border-[#444] group-hover:border-[#666]'
                            }`}
                            onClick={() => { set(field, !formData[field]); clearError(field); }}
                          >
                            {formData[field] && (
                              <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-foreground">{label}</span>
                        </label>
                        {errors[field] && <p className={`${errorCls} mt-1`}>{errors[field]}</p>}
                      </div>
                    ))}
                  </div>

                  <div className="border border-[#2a2a2a] p-5 bg-[#080808]">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Why this matters</p>
                    <p className="text-sm text-muted-foreground">
                      We've seen clients invest significantly in brand building only to face cease-and-desist letters later. These confirmations protect both of us and ensure the work we do together has a solid legal foundation.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 5: Final Details ── */}
            {currentStep === 5 && (
              <div>
                <div className="mb-10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-primary mb-2 font-semibold">Step 5 of 5</p>
                  <h2 className="text-3xl font-bold mb-3">Final details</h2>
                  <p className="text-muted-foreground">Last step. Help us understand your goals so we can hit the ground running if you're accepted.</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className={labelCls}>Project Context *</label>
                    <textarea
                      className={`${inputCls('projectContext')} resize-none`}
                      rows={4}
                      value={formData.projectContext}
                      onChange={(e) => { set('projectContext', e.target.value); clearError('projectContext'); }}
                      placeholder="Briefly describe your business, what you sell, and where you are in your growth journey."
                    />
                    {errors.projectContext && <p className={errorCls}>{errors.projectContext}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Founder / Team Size *</label>
                      <select className={inputCls('founderTeamSize')} value={formData.founderTeamSize} onChange={(e) => { set('founderTeamSize', e.target.value); clearError('founderTeamSize'); }}>
                        <option value="">Select team size</option>
                        <option value="solo-founder">Solo founder</option>
                        <option value="2-5">2–5 people</option>
                        <option value="6-15">6–15 people</option>
                        <option value="16-50">16–50 people</option>
                        <option value="50+">50+ people</option>
                      </select>
                      {errors.founderTeamSize && <p className={errorCls}>{errors.founderTeamSize}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Current Annual Revenue *</label>
                      <select className={inputCls('currentRevenue')} value={formData.currentRevenue} onChange={(e) => { set('currentRevenue', e.target.value); clearError('currentRevenue'); }}>
                        <option value="">Select revenue range</option>
                        <option value="pre-revenue">Pre-revenue</option>
                        <option value="under-100k">Under $100K</option>
                        <option value="100k-500k">$100K – $500K</option>
                        <option value="500k-1m">$500K – $1M</option>
                        <option value="1m-3m">$1M – $3M</option>
                        <option value="3m-10m">$3M – $10M</option>
                        <option value="10m+">$10M+</option>
                      </select>
                      {errors.currentRevenue && <p className={errorCls}>{errors.currentRevenue}</p>}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Current Bottleneck *</label>
                    <textarea
                      className={`${inputCls('currentBottleneck')} resize-none`}
                      rows={3}
                      value={formData.currentBottleneck}
                      onChange={(e) => { set('currentBottleneck', e.target.value); clearError('currentBottleneck'); }}
                      placeholder="What is the single biggest constraint holding your growth back right now?"
                    />
                    {errors.currentBottleneck && <p className={errorCls}>{errors.currentBottleneck}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>Preferred Timeline *</label>
                    <select className={inputCls('preferredTimeline')} value={formData.preferredTimeline} onChange={(e) => { set('preferredTimeline', e.target.value); clearError('preferredTimeline'); }}>
                      <option value="">Select timeline</option>
                      <option value="asap">ASAP — ready to start immediately</option>
                      <option value="1-month">Within 1 month</option>
                      <option value="1-3-months">1–3 months</option>
                      <option value="3-6-months">3–6 months</option>
                      <option value="exploring">Just exploring for now</option>
                    </select>
                    {errors.preferredTimeline && <p className={errorCls}>{errors.preferredTimeline}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>What are you trying to achieve in the next 90 days? *</label>
                    <textarea
                      className={`${inputCls('goals90Days')} resize-none`}
                      rows={4}
                      value={formData.goals90Days}
                      onChange={(e) => { set('goals90Days', e.target.value); clearError('goals90Days'); }}
                      placeholder="Be specific — revenue targets, launch dates, market expansion, etc."
                    />
                    {errors.goals90Days && <p className={errorCls}>{errors.goals90Days}</p>}
                  </div>

                  <div>
                    <label className={labelCls}>What is your biggest current marketing challenge? *</label>
                    <textarea
                      className={`${inputCls('biggestChallenge')} resize-none`}
                      rows={4}
                      value={formData.biggestChallenge}
                      onChange={(e) => { set('biggestChallenge', e.target.value); clearError('biggestChallenge'); }}
                      placeholder="What's not working, what's holding you back, or what you haven't been able to crack yet."
                    />
                    {errors.biggestChallenge && <p className={errorCls}>{errors.biggestChallenge}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>How did you hear about us? *</label>
                      <select className={inputCls('howHeard')} value={formData.howHeard} onChange={(e) => { set('howHeard', e.target.value); clearError('howHeard'); }}>
                        <option value="">Select one</option>
                        <option value="google">Google Search</option>
                        <option value="referral">Referral / Word of mouth</option>
                        <option value="social-media">Social Media</option>
                        <option value="linkedin">LinkedIn</option>
                        <option value="podcast">Podcast</option>
                        <option value="event">Event / Conference</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.howHeard && <p className={errorCls}>{errors.howHeard}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Preferred Contact Method *</label>
                      <select className={inputCls('preferredContact')} value={formData.preferredContact} onChange={(e) => { set('preferredContact', e.target.value); clearError('preferredContact'); }}>
                        <option value="">Select one</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone Call</option>
                        <option value="text">Text Message</option>
                      </select>
                      {errors.preferredContact && <p className={errorCls}>{errors.preferredContact}</p>}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Anything else you'd like us to know?</label>
                    <textarea
                      className={`${inputCls('anythingElse')} resize-none`}
                      rows={3}
                      value={formData.anythingElse}
                      onChange={(e) => set('anythingElse', e.target.value)}
                      placeholder="Optional — add any context that would help us prepare for our conversation."
                    />
                  </div>

                  {errors.submit && (
                    <div className="border border-red-500/30 bg-red-500/10 p-4">
                      <p className="text-red-400 text-sm">{errors.submit}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Navigation Buttons ── */}
            <div className="flex items-center justify-between mt-10 pt-8 border-t border-[#1a1a1a]">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="text-muted-foreground hover:text-foreground text-sm uppercase tracking-widest transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>
              ) : (
                <div />
              )}

              {currentStep < 5 ? (
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={handleNext}
                  disabled={isSubmitting}
                >
                  Continue
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>

          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}